/**
 * useCoverageScan — BLU-790.
 *
 * Replaces the single blocking `getProductSyncCoverage` call (a 40-page server
 * loop, 32-45s on Threadheads' 3,555-product catalogue, capped at 2,000) with a
 * SERIAL client-driven cursor walk over `getProductSyncCoveragePage`, folded
 * into the same store-wide aggregate via `@threadheads/shared`.
 *
 * Three properties this hook must preserve, each of which cost a review round:
 *
 * 1. **Serial, never parallel.** One page in flight at a time. Fanning out
 *    would multiply this org's Shopify call rate against a shared rate limit;
 *    the point is to unblock the render, not to spend the budget faster.
 *
 * 2. **Re-entering the tab must not re-spend the Shopify budget.** The fold
 *    itself is component state and is replayed on remount — that part is cheap
 *    and deliberate. What must NOT repeat is the network: each PAGE is fetched
 *    through the React Query cache with an explicit `staleTime`, so a tab flip
 *    within that window replays the walk against cached pages and issues zero
 *    Shopify calls. Without the explicit `staleTime` this would silently refetch
 *    every page — the app sets no default (`App.tsx:233-253`), so it is 0.
 *
 * 3. **`mappedCoverage` is derived once, at the end.** Never summed per page.
 *    See the module header of `product-sync-coverage.ts`.
 *
 * Progressive by design: `coverage` is non-null and usable after the FIRST
 * page, with `isComplete: false`. Consumers render real numbers immediately and
 * let them fill in, rather than blocking on the full walk.
 *
 * Phase 86: the walk itself (properties 1 and 2, the superseded-walk guard,
 * `MAX_PAGES`, `rescan`) now lives in the generic `useSerialCursorScan`, which
 * the Product updates worklist shares. This file keeps only the coverage fold
 * and its public return, which is unchanged.
 *
 * BLU-1373: each page request carries the tag (`{ cursor, syncTag }`), so the
 * React Query key includes it and a tag change can never replay another tag's
 * cached page. The server ignores the field and reads the SAVED tag; it is
 * cache identity only. The return also exposes `seenProductTypes`, every
 * product type the scan has seen (mapped or not), for the onboarding step
 * that asks Custom / Ready-made / Mixed per type.
 */
import { useMemo } from 'react';
import {
  createCoverageAccumulator,
  finalizeCoverage,
  foldCoveragePage,
  type CoverageAccumulator,
  type CoveragePage,
  type CoverageSummary,
} from '@threadheads/shared';
import { trpc } from '@/lib/trpc';
import { PAGE_STALE_MS, useSerialCursorScan } from './useSerialCursorScan';

export interface CoverageScanState {
  /** Folded store-wide picture. Non-null from the first page onward; null before it lands. */
  coverage: CoverageSummary | null;
  /** Products examined so far — the honest denominator for progress copy. */
  scannedProducts: number;
  /** True once Shopify reports no further pages. `coverage` is exhaustive only then. */
  isComplete: boolean;
  /** True while a page request is in flight. */
  isScanning: boolean;
  /** Set when a page request failed; the walk stops and keeps what it folded. */
  error: string | null;
  /**
   * Every product type seen so far (mapped or not), de-duplicated
   * case-insensitively (first spelling wins) and sorted. `[]` before the first page.
   */
  seenProductTypes: string[];
  /** Discard the fold and walk again from page 1. */
  rescan: () => void;
}

interface CoverageFold {
  coverage: CoverageSummary;
  scannedProducts: number;
  seenProductTypes: string[];
}

const NO_TYPES: string[] = [];

/** Case-insensitive de-dupe (first spelling wins, blanks dropped), sorted with `localeCompare`. */
function distinctTypeNames(names: Iterable<string>): string[] {
  const byKey = new Map<string, string>();
  for (const name of names) {
    const key = name.trim().toLowerCase();
    if (key !== '' && !byKey.has(key)) byKey.set(key, name.trim());
  }
  return [...byKey.values()].sort((a, b) => a.localeCompare(b));
}

/**
 * @param enabled Gate the walk (e.g. only on the tab that consumes it).
 * @param syncTag The org's saved gate tag — part of the cache identity, because
 *   changing it changes which products "the catalogue" means.
 */
export function useCoverageScan(enabled: boolean, syncTag: string | undefined): CoverageScanState {
  const utils = trpc.useUtils();

  const scan = useSerialCursorScan<CoveragePage, CoverageAccumulator, CoverageFold>({
    enabled,
    identity: syncTag ?? '',
    // `fetch` (not `useQuery`) because the walk is a sequence whose length is
    // unknown at render time. The explicit `staleTime` is load-bearing: this
    // app sets no default, so without it `fetchQuery` treats every cached page
    // as stale and re-hits Shopify on each tab flip.
    fetchPage: (cursor) =>
      // The tag rides in the input so the page cache is keyed by it.
      utils.productSync.getProductSyncCoveragePage.fetch(
        { cursor, syncTag: syncTag ?? '' },
        { staleTime: PAGE_STALE_MS },
      ),
    // Rescan must actually re-hit Shopify, not replay still-fresh cached pages.
    invalidate: () => utils.productSync.getProductSyncCoveragePage.invalidate(),
    createAcc: createCoverageAccumulator,
    fold: (acc, page) => {
      foldCoveragePage(acc, page);
    },
    // `mappedCoverage` is derived once per fold from the accumulator, never
    // summed per page.
    finalize: (acc, morePages) => ({
      coverage: finalizeCoverage(acc, morePages),
      scannedProducts: acc.totals.products,
      seenProductTypes: distinctTypeNames(acc.seenProductTypes),
    }),
  });

  const { result, isComplete, isScanning, error, rescan } = scan;
  return useMemo(
    () => ({
      coverage: result?.coverage ?? null,
      scannedProducts: result?.scannedProducts ?? 0,
      isComplete,
      isScanning,
      error,
      seenProductTypes: result?.seenProductTypes ?? NO_TYPES,
      rescan,
    }),
    [result, isComplete, isScanning, error, rescan],
  );
}
